import { useState, useCallback } from 'react';

interface UseDragAndDropOptions {
  onDrop: (files: FileList) => void;
  onRejectedFiles?: (rejectedFiles: File[]) => void;
  acceptedTypes?: string[];
  maxFiles?: number;
}

interface UseDragAndDropReturn {
  isDragOver: boolean;
  dragProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

export const useDragAndDrop = ({
  onDrop,
  onRejectedFiles,
  acceptedTypes,
  maxFiles,
}: UseDragAndDropOptions): UseDragAndDropReturn => {
  const [isDragOver, setIsDragOver] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dragCounter, setDragCounter] = useState(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setIsDragOver(false);
      }
      return newCount;
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);

      const files = e.dataTransfer.files;
      if (!files.length) return;

      // Filter by accepted types if specified
      let filteredFiles = Array.from(files);
      let rejectedFiles: File[] = [];

      if (acceptedTypes && acceptedTypes.length > 0) {
        const accepted: File[] = [];
        const rejected: File[] = [];

        filteredFiles.forEach((file) => {
          if (acceptedTypes.includes(file.type)) {
            accepted.push(file);
          } else {
            rejected.push(file);
          }
        });

        filteredFiles = accepted;
        rejectedFiles = rejected;
      }

      // Limit number of files if specified
      if (maxFiles && filteredFiles.length > maxFiles) {
        const excess = filteredFiles.slice(maxFiles);
        filteredFiles = filteredFiles.slice(0, maxFiles);
        rejectedFiles = [...rejectedFiles, ...excess];
      }

      // Call rejected files callback if any files were rejected
      if (rejectedFiles.length > 0 && onRejectedFiles) {
        onRejectedFiles(rejectedFiles);
      }

      // Only proceed if we have valid files
      if (filteredFiles.length > 0) {
        const fileList = new DataTransfer();
        filteredFiles.forEach((file) => fileList.items.add(file));
        onDrop(fileList.files);
      }
    },
    [onDrop, onRejectedFiles, acceptedTypes, maxFiles]
  );

  return {
    isDragOver,
    dragProps: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
  };
};
